import React, { useEffect, useState, useMemo, useContext, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Autocomplete,
  Popper,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CancelIcon from '@mui/icons-material/Cancel';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import EnumsService from '../../../Services/enums.service';
import AccountCardService from '../../../Services/accountcard.service';
import CashFlowForecastService from '../../../Services/cashFlowForecast.service';
import { toast } from 'react-toastify';
import UserService from '../../../Services/user.service';
import { validateEmail, validatePhoneNumber } from '../../../Utils/helpers';
import { isValidIDOrCompanyID } from '../../../Utils/validationHelpers';
import Constants from '../../../assests/Constants/constants';
import CustomDatePicker from '../CustomDates/CustomDatePicker/CustomDatePicker';
import AutocompleteSelectFilter from '../AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import { PermissionsContext } from '../../../Contexts/Permissions';
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import DocumentTypeYearModal from './DocumentTypeYearModal';
import DocumentsByTypeModal from './DocumentsByTypeModal';
import OcrRoutingRulesEditor from './OcrRoutingRulesEditor';
import AccountCardFormSection from './AccountCardFormSection';
import {
  createEmptyOcrRoutingConfig,
  parseOcrRoutingFromApi,
  serializeOcrRoutingForApi,
} from './ocrRoutingRulesUtils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import fieldStyles from '../../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import styles from './AddAccountCardModal.module.scss';
import { syncAccountCardToDocumentEnums } from '../../../Utils/cacheManager';

dayjs.extend(utc);

const MySwal = withReactContent(Swal);
const FILTER_Z = 10150;
const comboInputProps = { disableUnderline: true };
const datePickerInputSx = {
  border: '0 !important',
  borderRadius: '0 !important',
  background: 'transparent !important',
  height: '40px',
  minHeight: '40px',
  boxSizing: 'border-box',
  boxShadow: 'none !important',
  outline: 'none !important',
  padding: '0 8px',
  '&:hover, &:focus, &.Mui-focused, &:focus-within': {
    border: '0 !important',
    boxShadow: 'none !important',
  },
};

const sameWidthModifier = {
  name: 'sameWidth',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn: ({ state }) => {
    const width = `${state.rects.reference.width}px`;
    state.styles.popper.width = width;
    state.styles.popper.minWidth = width;
  },
};

const ComboPopper = React.forwardRef(({ direction = 'rtl', ...props }, ref) => {
  const { className, style, modifiers, ...other } = props;
  const width = props.anchorEl?.clientWidth ?? style?.width;
  return (
    <Popper
      {...other}
      ref={ref}
      dir={direction}
      className={`${className || ''} ${fieldStyles.comboPopper} ${styles.comboPopperHigh}`}
      placement={direction === 'rtl' ? 'bottom-end' : 'bottom-start'}
      modifiers={[
        ...(modifiers || []),
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'preventOverflow', options: { padding: 0 } },
        sameWidthModifier,
      ]}
      style={{
        ...style,
        width,
        minWidth: width,
        zIndex: FILTER_Z,
      }}
    />
  );
});
ComboPopper.displayName = 'ComboPopper';

const FilterSelect = ({ direction, ...props }) => (
  <AutocompleteSelectFilter
    isMultiple={false}
    direction={direction}
    portalMenu
    searchable
    portalZIndex={FILTER_Z}
    {...props}
  />
);

const DEFAULT_OPEN_SECTIONS = {
  general: true,
  contact: true,
  bank: false,
  withholding: false,
  paymentTerms: false,
  classification: false,
  cashFlow: false,
  ocr: false,
  documents: false,
};

const ACCOUNTANT_ONLY_SECTIONS = ['classification', 'cashFlow', 'ocr'];

function PaperComponent(props) {
  const nodeRef = useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'.add-account-card-modal-no-drag, .MuiIconButton-root, button, [class*="MuiDialogContent-root"]'}
    >
      <div ref={nodeRef} {...props} />
    </Draggable>
  );
}

const AddAccountCardModal = ({
  open,
  onClose,
  editedItem,
  isRange,
  isClosedReportingMonth,
  palCodes,
  propPalCode,
  direction,
  client,
  token,
  setLoading: setLoadingProp,
  accountCardOptions,
  handleChangeAfterAdd,
  fetchEnums,
  type = 'main',
  isEdit,
  TrialBalanceSum = 0,
  sortCodes = [],
  assetGroups = [],
  requirePalCode = false,
}) => {
  const setLoading = typeof setLoadingProp === 'function' ? setLoadingProp : () => {};
  const { t } = useTranslation();
  const { userTypeContext } = useContext(PermissionsContext);

  /* ---------------- state ---------------- */
  const [accountCaseId, setAccountCaseId] = useState('');
  const [accountCodeName, setAccountCodeName] = useState('');
  const [oldCardId, setOldCardId] = useState('');
  const [oldName, setOldName] = useState('');
  const [oldAccountCaseId, setOldAccountCaseId] = useState('');
  const [accountCodeId, setAccountCodeId] = useState('');
  const [suggestedCardId, setSuggestedCardId] = useState('');
  const [palCode, setPalCode] = useState('');
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [duplicateCard, setDuplicateCard] = useState(null);
  const isDisabled = editedItem && editedItem.isEditable === false;
  
  // State for depreciation account card naming
  const [selectedAssetGroup, setSelectedAssetGroup] = useState(null);
  const [namePrefix, setNamePrefix] = useState('');
  const [isDepreciationAccount, setIsDepreciationAccount] = useState(false);

  /* -------- extra fields -------- */
  const [email, setEmail] = useState('');
  const [location, setLocation] = useState('');
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumber2, setPhoneNumber2] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [bankName, setBankName] = useState('');
  const [branchCode, setBranchCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [openSections, setOpenSections] = useState(DEFAULT_OPEN_SECTIONS);
  const [sourceDeductionAssociation, setSourceDeductionAssociation] = useState(
    ''
  );
  const [exactOccupation, setExactOccupation] = useState('');
  const [externalSoftwareCardNumber, setExternalSoftwareCardNumber] = useState('');
  const [deductionType, setDeductionType] = useState('');
  const [taxAssessor, setTaxAssessor] = useState('');
  const [deductionValidity, setDeductionValidity] = useState('');
  const [deductionRate, setDeductionRate] = useState('');

  // Payment terms (drives Accounts Receivable aging / due-date logic)
  const [paymentTermsType, setPaymentTermsType] = useState('');
  const [paymentTermsCustomDays, setPaymentTermsCustomDays] = useState('');
  
  // New fields for automatic processing (only for accountants)
  const [sortCodeId, setSortCodeId] = useState('');
  const [autoProcessed, setAutoProcessed] = useState(false);
  const [frequency, setFrequency] = useState('');
  const [ocrRoutingConfig, setOcrRoutingConfig] = useState(() => createEmptyOcrRoutingConfig('debit'));

  // Cash Flow Forecast (דוח תזרים מזומנים) configuration. Only cards marked
  // includeInCashFlow with a cash-flow code take part in the forecast report.
  const [includeInCashFlow, setIncludeInCashFlow] = useState(false);
  const [cashFlowCode, setCashFlowCode] = useState('');
  const [cashFlowAverageAmount, setCashFlowAverageAmount] = useState('');
  const [averageCalculating, setAverageCalculating] = useState(false);
  const [averageCalcNote, setAverageCalcNote] = useState('');
  // The cash flows defined for the case, so the card is assigned to one of them rather than
  // to a free-typed number that may not correspond to anything.
  const [cashFlowOptions, setCashFlowOptions] = useState([]);

  const [showAccountCaseIdExistsModal, setShowAccountCaseIdExistsModal] =
    useState(false);
  const [matchingCards, setMatchingCards] = useState([]);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [localSortCodeOptions, setLocalSortCodeOptions] = useState([]);
  const [localPalCodeOptions, setLocalPalCodeOptions] = useState([]);
  const [isSwapping, setIsSwapping] = useState(false);
  
  /* -------- document upload state -------- */
  const documentInputRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [showPreview, setShowPreview] = useState(true);
  const [documentUrls, setDocumentUrls] = useState([]); // Existing documents from DB (for backward compatibility)
  const [existingDocuments, setExistingDocuments] = useState([]); // Existing documents with full structure (Id, DocumentType, etc.)
  const [showDocumentTypeModal, setShowDocumentTypeModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // Files waiting for document type/year selection
  const [showDocumentsByTypeModal, setShowDocumentsByTypeModal] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null); // Selected document type for modal
  const [modalPreviewIndex, setModalPreviewIndex] = useState(0);
  const [showModalPreview, setShowModalPreview] = useState(true);

  /* ---------------- options ---------------- */
  const [sourceDeductionOptions] = useState([
    { key: 0, label: t('NotIncluded') },
    { key: 1, label: t('Included') },
  ]);

  const formattedPalCodeOptions = useMemo(() => {
    if (localPalCodeOptions.length > 0) {
      return localPalCodeOptions;
    }
    return (palCodes || []).map((pc) => ({
      label: pc.palCodeId ?? pc.label ?? pc.value ?? '',
      value: pc.palCodeId ?? pc.value ?? pc.label ?? '',
    }));
  }, [localPalCodeOptions, palCodes]);


  const formattedSortCodeOptions = useMemo(() => {
    if (localSortCodeOptions.length > 0) {
      return localSortCodeOptions;
    }
    return (sortCodes || []).map((sc) => {
      const name = sc.name ?? sc.sortCodeName ?? sc.Name ?? '';
      const id = sc.sortCodeId ?? sc.SortCodeId ?? sc.id ?? sc.Id ?? sc.value ?? sc.label ?? '';
      return {
        label: name && id ? `${name} (${id})` : (name || id || '—'),
        value: id,
      };
    });
  }, [localSortCodeOptions, sortCodes]);

  const sourceDeductionFilterOptions = useMemo(
    () => sourceDeductionOptions.map((o) => ({ value: o.key, label: o.label })),
    [sourceDeductionOptions]
  );

  const taxAssessorFilterOptions = useMemo(
    () => Constants.TaxAssessorOptionsString.map((o) => ({
      value: o.value,
      label: `${o.value} - ${o.label}`,
    })),
    []
  );

  const paymentTermsFilterOptions = useMemo(
    () => Constants.PaymentTermsTypeOptions.map((o) => ({
      value: o.value,
      label: t(`PaymentTerms_${o.label}`, o.label),
    })),
    [t]
  );

  const frequencyFilterOptions = useMemo(
    () => Constants.ExpenseFrequencyOptions.map((o) => ({
      value: o.value,
      label: t(`ExpenseFrequency_${o.label}`, o.label),
    })),
    [t]
  );

  const cashFlowFilterOptions = useMemo(
    () => cashFlowOptions.map((o) => ({
      value: o.code,
      label: `${o.code} · ${o.label || `${t('CashFlow', 'תזרים')} ${o.code}`}`,
    })),
    [cashFlowOptions, t]
  );

  const bankFilterOptions = useMemo(() => {
    const options = Constants.ISRAEL_BANKS.map((b) => ({
      value: b.code,
      label: `${b.code} - ${b.name}`,
    }));
    // Keep a stored code that is not in the list selectable instead of silently showing it empty.
    if (bankCode && !options.some((o) => o.value === bankCode)) {
      options.push({ value: bankCode, label: bankName ? `${bankCode} - ${bankName}` : bankCode });
    }
    return options;
  }, [bankCode, bankName]);

  /* ---------------- helpers ---------------- */
  /** Empty UI state ('') → null for API; preserves 0 (e.g. SourceDeduction not included). */
  const toNullableInt = (v) => {
    if (v === '' || v === undefined || v === null) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  /** Empty UI state ('') → null for API, keeping decimals. */
  const toNullableDecimal = (v) => {
    if (v === '' || v === undefined || v === null) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  /**
   * Cash-flow fields as the API expects them. Code and average amount only apply while the
   * card is included in the forecast; unchecking clears them. Currency is not stored per
   * card — report amounts come from the transactions (shekel or foreign debit/credit).
   */
  const cashFlowApiFields = {
    IncludeInCashFlow: includeInCashFlow,
    CashFlowCode: includeInCashFlow ? toNullableInt(cashFlowCode) : null,
    CashFlowAverageAmount: includeInCashFlow ? toNullableDecimal(cashFlowAverageAmount) : null,
    CashFlowCurrency: null,
  };

  const additionalDetailsApiFields = {
    PhoneNumber2: phoneNumber2 || null,
    BankCode: bankCode || null,
    BankName: bankName || null,
    BranchCode: branchCode || null,
    AccountNumber: accountNumber || null,
  };

  const syncCreatedAccountCardToSharedCache = (cardId) => {
    const caseId = client?.caseId;
    if (!caseId || cardId == null || cardId === '') return;
    syncAccountCardToDocumentEnums(
      caseId,
      {
        cardId: String(cardId),
        name: accountCodeName,
        accountCaseId: accountCaseId || '',
        palCode: palCode || null,
        email: email || null,
        phoneNumber: phoneNumber || null,
        location: location || null,
        isEditable: true,
      },
      isEdit ? oldCardId : null
    );
  };

  const dirClass = direction === 'rtl' ? styles.rtl : styles.ltr;
  const fieldTextAlign = direction === 'ltr' ? 'left' : 'right';
  const inputClassName = (hasError) =>
    `${fieldStyles.styleInput} ${hasError ? fieldStyles.styleInputRequired : ''} ${direction === 'rtl' ? fieldStyles.rtl : fieldStyles.ltr}`;
  const renderComboPopper = useCallback(
    (props) => <ComboPopper {...props} direction={direction} />,
    [direction]
  );

  /* ------------ effects ------------ */
  useEffect(() => {
    if (!accountCodeId) {
      setIsDuplicate(false);
      setDuplicateCard(null);
      return;
    }
    const found = (accountCardOptions || []).find(
      (card) => card.cardId === accountCodeId && card.cardId !== oldCardId
    );
    setIsDuplicate(!!found);
    setDuplicateCard(found || null);
  }, [accountCodeId, accountCardOptions, oldCardId]);

  const fetchCard = async (caseId, cardId) => {
    try {
      const res = await EnumsService.GetAccountCardByCardId(
        caseId,
        cardId,
        token
      );
      if (res.ok) {
        const card = await res.json();
        console.log('card', card);
        
        setEmail(card?.email || '');
        setLocation(card?.location || '');
        setDeliveryLocation(card?.deliveryLocation || card?.DeliveryLocation || '');
        setPhoneNumber(card?.phoneNumber || '');
        setPhoneNumber2(card?.phoneNumber2 ?? card?.PhoneNumber2 ?? '');
        setBankCode(card?.bankCode ?? card?.BankCode ?? '');
        setBankName(card?.bankName ?? card?.BankName ?? '');
        setBranchCode(card?.branchCode ?? card?.BranchCode ?? '');
        setAccountNumber(card?.accountNumber ?? card?.AccountNumber ?? '');
        setSourceDeductionAssociation(
          card?.sourceDeductionAssociation ?? ''
        );
        setExactOccupation(card?.exactOccupation || '');
        setExternalSoftwareCardNumber(
          card?.externalSoftwareCardNumber ?? card?.ExternalSoftwareCardNumber ?? ''
        );
        setDeductionType(card?.deductionType ?? '');
        setTaxAssessor(card?.taxAssessor || '');
        setDeductionValidity(card?.deductionValidity || '');
        setDeductionRate(card?.deductionRate?.toString() || '');
        // Payment terms — the days column (PaymentTermsCustomDays) is the canonical
        // source of truth. We reverse-derive the preset selection from it: preset
        // values (Net15/30/45/60/90) match exactly, anything else falls back to
        // Custom with the typed day count. The legacy enum column is read only as
        // a last-resort fallback (kept for backward-compat with old data).
        const cardDays = card?.paymentTermsCustomDays ?? card?.PaymentTermsCustomDays;
        const cardEnum = card?.paymentTermsType ?? card?.PaymentTermsType;
        if (cardDays != null) {
            const days = Number(cardDays);
            const presetValues = Object.values(Constants.PaymentTermsType ?? {})
                .filter((v) => v !== Constants.PaymentTermsType?.Custom);
            if (presetValues.includes(days)) {
                setPaymentTermsType(days);
                setPaymentTermsCustomDays('');
            } else {
                setPaymentTermsType(Constants.PaymentTermsType?.Custom ?? 999);
                setPaymentTermsCustomDays(String(days));
            }
        } else if (cardEnum != null) {
            setPaymentTermsType(Number(cardEnum));
            setPaymentTermsCustomDays('');
        } else {
            setPaymentTermsType('');
            setPaymentTermsCustomDays('');
        }
        // Set sort code and auto processed fields (support both camelCase and PascalCase from API)
        const cardSortCodeId = card?.sortCodeId ?? card?.SortCodeId;
        setSortCodeId(cardSortCodeId != null && cardSortCodeId !== '' ? String(cardSortCodeId) : '');
        setAutoProcessed(card?.autoProcessed ?? card?.AutoProcessed ?? false);
        const cardFrequency = card?.frequency ?? card?.Frequency;
        setFrequency(cardFrequency != null && cardFrequency !== '' ? Number(cardFrequency) : '');
        setIncludeInCashFlow(card?.includeInCashFlow ?? card?.IncludeInCashFlow ?? false);
        const cardCashFlowCode = card?.cashFlowCode ?? card?.CashFlowCode;
        setCashFlowCode(cardCashFlowCode != null && cardCashFlowCode !== '' ? Number(cardCashFlowCode) : '');
        const cardCashFlowAverage = card?.cashFlowAverageAmount ?? card?.CashFlowAverageAmount;
        setCashFlowAverageAmount(
          cardCashFlowAverage != null && cardCashFlowAverage !== '' ? String(cardCashFlowAverage) : ''
        );
        setAverageCalcNote('');
        setOcrRoutingConfig(
          parseOcrRoutingFromApi(
            card,
            (card?.palCode ?? card?.PalCode) === 'לקוחות' ? 'credit' : 'debit'
          )
        );
        
        // Set documents if they exist (new structure)
        // Handle both PascalCase (Documents) and camelCase (documents) for compatibility
        const documents = card?.Documents || card?.documents;
        if (documents && Array.isArray(documents) && documents.length > 0) {
          // New structure: Documents array - store full document structure with IDs
          setExistingDocuments(documents);
          // Also extract URLs for backward compatibility with existing UI
          const documentUrlsList = [];
          documents.forEach(doc => {
            // Handle new format: DocumentUrl (single string) - check both cases
            const documentUrl = doc.DocumentUrl || doc.documentUrl;
            if (documentUrl) {
              documentUrlsList.push(documentUrl);
            }
            // Backward compatibility: DocumentUrls (array) - check both cases
            else {
              const documentUrls = doc.DocumentUrls || doc.documentUrls;
              if (documentUrls && Array.isArray(documentUrls)) {
                documentUrlsList.push(...documentUrls);
              }
            }
          });
          setDocumentUrls(documentUrlsList);
        } else if (card?.documentUrls) {
          // Backward compatibility: old documentUrls format
          try {
            const urls = typeof card.documentUrls === 'string' 
              ? JSON.parse(card.documentUrls) 
              : card.documentUrls;
            setDocumentUrls(Array.isArray(urls) ? urls : []);
            setExistingDocuments([]); // No IDs available for old format
          } catch {
            setDocumentUrls([]);
            setExistingDocuments([]);
          }
        } else {
          console.log('No documents found'); // Debug log
          setDocumentUrls([]);
          setExistingDocuments([]);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSortCodes = async () => {
    try {
      const res = await EnumsService.GetSortCodesByAccountantId(client.accountantId, token);
      if (res.ok) {
        const sortCodesData = await res.json();
        const formatted = (sortCodesData || []).map((sortCode) => {
          const name = sortCode.name ?? sortCode.sortCodeName ?? sortCode.Name ?? '';
          const id = sortCode.sortCodeId ?? sortCode.SortCodeId ?? sortCode.id ?? sortCode.Id ?? '';
          return {
            label: name && id ? `${name} (${id})` : (name || String(id) || '—'),
            value: id,
          };
        });
        setLocalSortCodeOptions(formatted);
      }
    } catch (err) {
      console.error('Error fetching sort codes:', err);
    }
  };

  const fetchPalCodes = async () => {
    try {
      const res = await EnumsService.GetPalCodesByAccountantId(client.accountantId, token);
      if (res.ok) {
        const palCodesData = await res.json();
        const formatted = (palCodesData || []).map((palCode) => ({
          label: palCode.palCodeId,
          value: palCode.palCodeId,
        }));
        setLocalPalCodeOptions(formatted);
      }
    } catch (err) {
      console.error('Error fetching PAL codes:', err);
    }
  };

  /* -------- document upload handlers -------- */
  const handleDocumentClick = () => {
    documentInputRef.current?.click();
  };

  /* -------- document upload handlers -------- */
  /**
   * Handles file selection from file input.
   * IMPORTANT: This function shows the DocumentTypeYearModal BEFORE adding files to selectedDocuments.
   * Files are only added to selectedDocuments after user confirms document type and year in the modal.
   * Upload to server only happens when user clicks Save button.
   * 
   * Flow:
   * 1. User selects files → handleDocumentChange
   * 2. Files validated (type, size)
   * 3. Modal opens for document type/year selection
   * 4. User confirms → handleDocumentTypeConfirm → files added to selectedDocuments
   * 5. User clicks Save → files uploaded to server
   */
  const handleDocumentChange = (event) => {
    // Get single file (not multiple)
    const file = event.target.files?.[0];
    
    if (!file) {
      return;
    }
    
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    // Validate file type
    if (!validTypes.includes(file.type)) {
      toast.error(t("PleaseSelectValidFile") || `Invalid file type for ${file.name}`);
      // Reset file input
      if (documentInputRef.current) {
        documentInputRef.current.value = '';
      }
      return;
    }

    // Validate file size
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t("FileSizeTooLarge") || `File ${file.name} is too large (max 10MB)`);
      // Reset file input
      if (documentInputRef.current) {
        documentInputRef.current.value = '';
      }
      return;
    }

    // STEP 1: Store pending file (single file, NOT yet added to selectedDocuments)
    // STEP 2: Show modal - user MUST select document type and year before proceeding
    setPendingFiles([file]);
    setShowDocumentTypeModal(true);

    // Reset file input so same file can be selected again if needed
    if (documentInputRef.current) {
      documentInputRef.current.value = '';
    }
  };

  /**
   * Called when user confirms document type and year in the modal.
   * This is the ONLY place where files are added to selectedDocuments.
   * Files are NOT uploaded yet - they will be uploaded when user clicks Save.
   * 
   * @param {string} fileName - Generated filename: {year}_{documentType} or custom title
   * @param {number} year - Selected year
   * @param {string} documentType - Selected document type (Hebrew string)
   * @param {number} documentTypeEnum - Document type enum value (0-4)
   * @param {string} title - Document title (custom or generated)
   */
  const handleDocumentTypeConfirm = (fileName, year, documentType, documentTypeEnum, title) => {
    // STEP 3: User confirmed type/year - NOW add files to selectedDocuments
    // Files are still NOT uploaded - they will be uploaded on Save
    const newDocuments = pendingFiles.map(file => ({
      file,
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      customFileName: fileName, // Store custom filename: {year}_{documentType} or custom title
      year: year,
      documentType: documentType, // Hebrew string for display
      documentTypeEnum: documentTypeEnum, // Enum value for backend (0-4)
      title: title || fileName, // Use provided title or fallback to fileName
    }));

    // Add to selectedDocuments (ready for upload when Save is clicked)
    setSelectedDocuments(prev => [...prev, ...newDocuments]);
    setPendingFiles([]);
  };

  const removeDocument = (documentId) => {
    setSelectedDocuments(prev => prev.filter(doc => doc.id !== documentId));
  };

  const removeExistingDocument = async (urlToRemove) => {
    
    // Extract filename from URL for display
    const filename = urlToRemove.split('/').pop()?.split('?')[0] || "המסמך";

    try {
      // Show confirmation dialog with document preview
    // Use target: 'body' to ensure SweetAlert renders outside modal's DOM tree
    const result = await MySwal.fire({
      title: t("ConfirmDelete") || "אשר מחיקה",
      html: `
        <div style="text-align: center;">
          <p style="font-size: 16px; margin-bottom: 16px; color: #333;">
            ${t("AreYouSureDeleteDocument") || "האם אתה בטוח שברצונך למחוק את המסמך"}
          </p>
          <div style="
            width: 100%;
            max-width: 500px;
            height: 300px;
            margin: 0 auto 16px;
            border: 2px solid #e0e0e0;
            border-radius: 8px;
            overflow: hidden;
            background-color: #f5f5f5;
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <iframe
              src="${urlToRemove}#page=1&view=FitH"
              style="
                width: 100%;
                height: 100%;
                border: none;
                object-fit: contain;
              "
              title="Document Preview"
            ></iframe>
          </div>
          <p style="font-size: 14px; color: #666; word-break: break-all;">
            ${decodeURIComponent(filename)}
          </p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("YesDelete") || "כן, מחק",
      cancelButtonText: t("Cancel") || "ביטול",
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#304FFF",
      customClass: { 
        container: "swal-high-zindex",
        popup: "swal-high-zindex-popup"
      },
      width: "600px",
      zIndex: 10020,
      allowOutsideClick: false,
      allowEscapeKey: true,
    });


      // If user cancelled, return early
      if (!result.isConfirmed) {
        return;
      }
    } catch (error) {
      toast.error(t("FailedToShowConfirmation") || "Failed to show confirmation dialog");
      return;
    }

    setLoading(true);

    try {
      // Only delete from backend if we have a cardId (for existing cards)
      if (accountCodeId && accountCodeId.toString()) {
        // Delete from S3 and database
        await EnumsService.DeleteAccountCardDocument(
          urlToRemove,
          client.caseId,
          accountCodeId.toString(),
          token
        );
      }

      // Remove from UI state (works for both new and existing cards)
      setDocumentUrls(prev => prev.filter(url => {
        const urlWithoutParams = url.split('?')[0];
        const removeUrlWithoutParams = urlToRemove.split('?')[0];
        return urlWithoutParams !== removeUrlWithoutParams;
      }));
      
      // Also remove from existingDocuments if it exists there
      setExistingDocuments(prev => {
        const updated = prev.map(doc => {
          // Handle new format: DocumentUrl (single string)
          const docUrl = doc.DocumentUrl || doc.documentUrl;
          if (docUrl) {
            const docUrlWithoutParams = docUrl.split('?')[0];
            const removeUrlWithoutParams = urlToRemove.split('?')[0];
            if (docUrlWithoutParams === removeUrlWithoutParams) {
              // Remove the entire document if URL matches
              return null;
            }
          }
          // Backward compatibility: DocumentUrls (array)
          if (doc.DocumentUrls && Array.isArray(doc.DocumentUrls)) {
            const filteredUrls = doc.DocumentUrls.filter(url => {
              const urlWithoutParams = url.split('?')[0];
              const removeUrlWithoutParams = urlToRemove.split('?')[0];
              return urlWithoutParams !== removeUrlWithoutParams;
            });
            if (filteredUrls.length === 0) {
              // If no URLs left, remove the entire document
              return null;
            }
            return {
              ...doc,
              DocumentUrls: filteredUrls
            };
          }
          return doc;
        }).filter(doc => doc !== null);
        
        return updated;
      });

      toast.success(t("DocumentDeletedSuccessfully") || "Document deleted successfully");
    } catch (error) {
      console.error("Failed to delete document:", error);
      toast.error(t("FailedToDeleteDocument") || "Failed to delete document");
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getAllDocuments = () => {
    const existingDocs = (documentUrls || []).map((url, idx) => ({
      url,
      isExisting: true,
      id: `existing-${idx}`,
      name: url.split('/').pop()?.split('?')[0] || `Document ${idx + 1}`,
    }));
    
    const newDocs = selectedDocuments.map((doc) => ({
      url: URL.createObjectURL(doc.file),
      isExisting: false,
      id: doc.id,
      name: doc.name,
      file: doc.file,
    }));
    
    return [...existingDocs, ...newDocs];
  };

  // Get document counts by type
  const getDocumentCountsByType = useMemo(() => {
    const counts = {};
    const unsavedCounts = {};
    Constants.AccountCardDocumentTypes.forEach(docType => {
      counts[docType.enumValue] = 0;
      unsavedCounts[docType.enumValue] = 0;
    });
    
    // Count existing documents (saved)
    existingDocuments.forEach(doc => {
      const docType = doc.DocumentType !== undefined ? doc.DocumentType : doc.documentType;
      if (docType !== undefined && counts[docType] !== undefined) {
        counts[docType]++;
      }
    });
    
    // Count new documents (unsaved)
    selectedDocuments.forEach(doc => {
      if (doc.documentTypeEnum !== undefined && counts[doc.documentTypeEnum] !== undefined) {
        counts[doc.documentTypeEnum]++;
        unsavedCounts[doc.documentTypeEnum]++;
      }
    });
    
    return { counts, unsavedCounts };
  }, [existingDocuments, selectedDocuments]);

  // Get documents by type for the modal
  const getDocumentsByType = (documentTypeEnum) => {
    const docs = [];
    
      // Add existing documents of this type
      existingDocuments.forEach((doc, idx) => {
        const docType = doc.DocumentType !== undefined ? doc.DocumentType : doc.documentType;
        if (docType === documentTypeEnum) {
          const url = doc.DocumentUrl || doc.documentUrl;
          if (url) {
            // Get display name: use Title if available, otherwise construct from DocumentType and Year
            let displayName = '';
            if (doc.Title) {
              displayName = doc.Title;
            } else if (docType !== undefined && (doc.Year || doc.year)) {
              const docTypeName = Constants.AccountCardDocumentTypes.find(
                dt => dt.enumValue === docType
              )?.value || 'מסמך';
              displayName = `${docTypeName}_${doc.Year || doc.year}`;
            } else {
              displayName = url.split('/').pop()?.split('?')[0] || `Document ${idx + 1}`;
            }
            
            docs.push({
              url,
              isExisting: true,
              id: doc.Id || `existing-${idx}`,
              name: url.split('/').pop()?.split('?')[0] || `Document ${idx + 1}`,
              displayName: displayName,
              documentType: docType,
              year: doc.Year || doc.year,
              title: doc.Title || doc.title,
              fullDoc: doc, // Store full document for deletion
            });
          }
        }
      });
      
      // Add new documents of this type
      selectedDocuments.forEach(doc => {
        if (doc.documentTypeEnum === documentTypeEnum) {
          docs.push({
            url: URL.createObjectURL(doc.file),
            isExisting: false,
            id: doc.id,
            name: doc.name,
            file: doc.file,
            displayName: doc.title || doc.customFileName || doc.name,
            documentType: doc.documentTypeEnum,
            year: doc.year,
            title: doc.title || doc.customFileName,
            fullDoc: doc, // Store full document for deletion
          });
        }
      });
    
    return docs;
  };

  // Handle opening documents by type modal
  const handleOpenDocumentsByType = (documentTypeEnum) => {
    setSelectedDocumentType(documentTypeEnum);
    setModalPreviewIndex(0);
    setShowModalPreview(true);
    setShowDocumentsByTypeModal(true);
  };

  // Handle file selection for documents by type modal
  const handleDocumentChangeForType = (event, documentTypeEnum) => {
    const file = event.target.files?.[0];
    
    if (!file) {
      return;
    }
    
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    if (!validTypes.includes(file.type)) {
      toast.error(t("PleaseSelectValidFile") || `Invalid file type for ${file.name}`);
      const modalFileInput = document.getElementById('modal-file-input');
      if (modalFileInput) {
        modalFileInput.value = '';
      }
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(t("FileSizeTooLarge") || `File ${file.name} is too large (max 10MB)`);
      const modalFileInput = document.getElementById('modal-file-input');
      if (modalFileInput) {
        modalFileInput.value = '';
      }
      return;
    }

    // Store file and pre-select document type
    setPendingFiles([file]);
    setShowDocumentTypeModal(true);
    // Note: DocumentTypeYearModal will need to accept preselected type
    // For now, user will need to select the type in the modal
    
    const modalFileInput = document.getElementById('modal-file-input');
    if (modalFileInput) {
      modalFileInput.value = '';
    }
  };

  const allDocuments = getAllDocuments();
  const currentDocument = allDocuments[currentDocIndex];
  const hasDocuments = allDocuments.length > 0;

  const handleTogglePreview = () => {
    setShowPreview((prev) => !prev);
  };

  const handleNextDocument = () => {
    setCurrentDocIndex((prev) => (prev + 1) % allDocuments.length);
  };

  const handlePreviousDocument = () => {
    setCurrentDocIndex((prev) => (prev - 1 + allDocuments.length) % allDocuments.length);
  };

  useEffect(() => {
    if (!open) {
      setSelectedDocuments([]);
      setCurrentDocIndex(0);
      setDocumentUrls([]);
      setExistingDocuments([]);
      setShowDocumentsByTypeModal(false);
      setSelectedDocumentType(null);
      setModalPreviewIndex(0);
      setShowModalPreview(true);
      setSuggestedCardId('');
      setOpenSections(DEFAULT_OPEN_SECTIONS);
    }
  }, [open]);

    useEffect(() => {
    // Only initialize when modal opens for the first time, not on every editedItem change
    if (!open) {
      hasInitializedRef.current = false;
      return;
    }
    
    // If already initialized, don't reset the values (allows user to type freely)
    if (hasInitializedRef.current) return;
    
    // Wait for editedItem to be available (especially important when opening from right-click)
    if (!editedItem) return;
    
    hasInitializedRef.current = true;

    /* ---- IDs & names ---- */
    if (isEdit) {
      setOldCardId(editedItem.accountCodeId || editedItem.cardId || '');
      setOldName(editedItem.accountCodeName || editedItem.name || '');
      setOldAccountCaseId(editedItem.accountCaseId || '');
    }

    setAccountCaseId(editedItem.accountCaseId || '');
    
    // Handle depreciation account card naming
    const initialName = editedItem.accountCodeName || editedItem.name || '';
    const debitPrefix = Constants.DepreciationPrefixes.DebitAccount;
    const creditPrefix = Constants.DepreciationPrefixes.CreditAccount;
    
    let prefix = '';
    let groupName = '';
    let isDepreciation = false;
    
    if (initialName.startsWith(debitPrefix)) {
      prefix = debitPrefix;
      groupName = initialName.replace(debitPrefix, '');
      isDepreciation = true;
    } else if (initialName.startsWith(creditPrefix)) {
      prefix = creditPrefix;
      groupName = initialName.replace(creditPrefix, '');
      isDepreciation = true;
    } else if ((type === 'debitAccount' || type === 'creditAccount') && assetGroups.length > 0) {
      // New account card - set prefix based on type
      if (type === 'debitAccount') {
        prefix = debitPrefix;
      } else if (type === 'creditAccount') {
        prefix = creditPrefix;
      }
      isDepreciation = true;
    }
    
    if (isDepreciation && groupName) {
      // Find the asset group by name
      const group = assetGroups.find(g => g.assetGroupName === groupName);
      setSelectedAssetGroup(group || null);
    } else {
      setSelectedAssetGroup(null);
    }
    
    setNamePrefix(prefix);
    setIsDepreciationAccount(isDepreciation);
    setAccountCodeName(initialName);
    const prefilledCardId = editedItem.accountCodeId ?? editedItem.cardId ?? '';
    if (isEdit || prefilledCardId) {
      setAccountCodeId(prefilledCardId ? String(prefilledCardId) : '');
    } else {
      setAccountCodeId('');
    }
    const extCardNum =
      editedItem?.externalSoftwareCardNumber ?? editedItem?.ExternalSoftwareCardNumber;
    if (!isEdit && extCardNum != null) {
      setExternalSoftwareCardNumber(String(extCardNum));
    }

    /* ---- Pal-code ---- */
    // Priority: explicit prop  → value from row  → empty string
    const initialPal = propPalCode ?? editedItem.palCode ?? '';
    setPalCode(initialPal);

    /* ---- SortCode and AutoProcessed (support both camelCase and PascalCase from API) ---- */
    const initialSortCodeId = editedItem?.sortCodeId ?? editedItem?.SortCodeId;
    setSortCodeId(initialSortCodeId != null && initialSortCodeId !== '' ? String(initialSortCodeId) : '');
    setAutoProcessed(editedItem?.autoProcessed ?? editedItem?.AutoProcessed ?? false);
    const initialFrequency = editedItem?.frequency ?? editedItem?.Frequency;
    setFrequency(initialFrequency != null && initialFrequency !== '' ? Number(initialFrequency) : '');
    setIncludeInCashFlow(editedItem?.includeInCashFlow ?? editedItem?.IncludeInCashFlow ?? false);
    const initialCashFlowCode = editedItem?.cashFlowCode ?? editedItem?.CashFlowCode;
    setCashFlowCode(
      initialCashFlowCode != null && initialCashFlowCode !== '' ? Number(initialCashFlowCode) : ''
    );
    const initialCashFlowAverage =
      editedItem?.cashFlowAverageAmount ?? editedItem?.CashFlowAverageAmount;
    setCashFlowAverageAmount(
      initialCashFlowAverage != null && initialCashFlowAverage !== ''
        ? String(initialCashFlowAverage)
        : ''
    );
    setAverageCalcNote('');
    setOcrRoutingConfig(
      parseOcrRoutingFromApi(
        editedItem,
        initialPal === 'לקוחות' ? 'credit' : 'debit'
      )
    );
  }, [open, editedItem, isEdit, propPalCode, type, assetGroups]);

  // When opening for add without a prefilled card id: fetch next card ID from API
  useEffect(() => {
    if (!open || isEdit || !client?.caseId || !token) return;

    const prefilledCardId = editedItem?.accountCodeId ?? editedItem?.cardId;
    if (prefilledCardId) return;

    let cancelled = false;
    (async () => {
      try {
        const nextId = await EnumsService.GetNextCardId(client.caseId, null, token);
        if (cancelled) return;
        const nextIdStr = nextId != null ? String(nextId) : '';
        if (nextIdStr) {
          setSuggestedCardId(nextIdStr);
          setAccountCodeId(nextIdStr);
        }
      } catch (err) {
        if (!cancelled) console.warn('Could not get next card ID on open:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isEdit, client?.caseId, token, editedItem?.accountCodeId, editedItem?.cardId]);

  useEffect(() => {
    if (open && isEdit && oldCardId) {
      fetchCard(client?.caseId, oldCardId);
    }
    if (open && !isEdit) {
      setEmail('');
      setLocation('');
      setDeliveryLocation('');
      setPhoneNumber('');
      setPhoneNumber2('');
      setBankCode('');
      setBankName('');
      setBranchCode('');
      setAccountNumber('');
      setSourceDeductionAssociation('');
      setExactOccupation('');
      setExternalSoftwareCardNumber(
        editedItem?.externalSoftwareCardNumber ?? editedItem?.ExternalSoftwareCardNumber ?? ''
      );
      setDeductionType('');
      setTaxAssessor('');
      setDeductionValidity('');
      setDeductionRate('');
      setPaymentTermsType('');
      setPaymentTermsCustomDays('');
      setSortCodeId('');
      setAutoProcessed(false);
      setFrequency('');
      setIncludeInCashFlow(false);
      setCashFlowCode('');
      setCashFlowAverageAmount('');
      setAverageCalcNote('');
      setOcrRoutingConfig(createEmptyOcrRoutingConfig('debit'));
      setExistingDocuments([]); // Clear existing documents for new cards
    }
    
    // Fetch sort codes and PAL codes when modal opens
    if (open && client?.accountantId && token) {
      fetchSortCodes();
      fetchPalCodes();
    }
    if (!open) {
      setLocalSortCodeOptions([]);
      setLocalPalCodeOptions([]);
    }
  }, [open, isEdit, oldCardId, client, userTypeContext, editedItem]);

  /* ------------ validation & save-disabled ------------ */
  // Check if cardName is valid (for depreciation accounts, must have content after prefix)
  const isCardNameValid = useMemo(() => {
    if (!accountCodeName) return false;
    const trimmedName = accountCodeName.trim();
    if (trimmedName === '') return false;
    
    // For depreciation accounts, check if it's only the prefix
    if (isDepreciationAccount && namePrefix) {
      return trimmedName !== namePrefix && trimmedName.length > namePrefix.length;
    }
    
    // For non-depreciation accounts, just check if it's not empty
    return trimmedName !== '';
  }, [accountCodeName, isDepreciationAccount, namePrefix]);

  const fieldErrors = useMemo(
    () => ({
      cardId: accountCodeId?.toString().trim() === '',
      cardName: !isCardNameValid,
      email: email !== '' && !validateEmail(email),
      phone: phoneNumber !== '' && !validatePhoneNumber(phoneNumber),
      phone2: phoneNumber2 !== '' && !validatePhoneNumber(phoneNumber2),
      companyId:
        accountCaseId !== '' && !isValidIDOrCompanyID(accountCaseId),
      duplicate: isDuplicate,
      palCode: requirePalCode && !palCode,
    }),
    [accountCodeId, isCardNameValid, email, phoneNumber, phoneNumber2, accountCaseId, isDuplicate, requirePalCode, palCode]
  );

  /* ------------ sections ------------ */
  const isClientUser = userTypeContext?.toString() === Constants.UserType.Client;
  const visibleSectionIds = Object.keys(DEFAULT_OPEN_SECTIONS).filter(
    (id) => !isClientUser || !ACCOUNTANT_ONLY_SECTIONS.includes(id)
  );
  const allSectionsOpen = visibleSectionIds.every((id) => openSections[id]);

  const toggleAllSections = () => {
    const next = !allSectionsOpen;
    setOpenSections((prev) => {
      const updated = { ...prev };
      visibleSectionIds.forEach((id) => {
        updated[id] = next;
      });
      return updated;
    });
  };

  const sectionProps = (id) => ({
    open: !!openSections[id],
    onToggle: () => setOpenSections((prev) => ({ ...prev, [id]: !prev[id] })),
  });

  const joinSummary = (parts) => parts.filter(Boolean).join(' · ');

  const selectedPaymentTermsLabel =
    paymentTermsType === '' || paymentTermsType == null
      ? ''
      : paymentTermsFilterOptions.find((o) => Number(o.value) === Number(paymentTermsType))?.label || '';

  const totalDocumentsCount = Object.values(getDocumentCountsByType.counts).reduce(
    (sum, count) => sum + count,
    0
  );

  const sectionSummaries = {
    general: joinSummary([accountCodeId, accountCodeName, palCode]),
    contact: joinSummary([phoneNumber, phoneNumber2, email, location]),
    bank: joinSummary([
      bankCode && bankName ? `${bankCode} - ${bankName}` : bankCode || bankName,
      branchCode && `${t('BranchNumber')} ${branchCode}`,
      accountNumber && `${t('AccountNumber')} ${accountNumber}`,
    ]),
    withholding: joinSummary([
      deductionRate !== '' && `${deductionRate}%`,
      deductionValidity && dayjs(deductionValidity).format('DD/MM/YYYY'),
    ]),
    paymentTerms: joinSummary([
      selectedPaymentTermsLabel,
      Number(paymentTermsType) === Constants.PaymentTermsType.Custom && paymentTermsCustomDays,
    ]),
    classification: joinSummary([
      formattedSortCodeOptions.find((o) => String(o.value) === String(sortCodeId))?.label,
      autoProcessed && t('AutoProcessed'),
    ]),
    cashFlow: includeInCashFlow
      ? cashFlowFilterOptions.find((o) => Number(o.value) === Number(cashFlowCode))?.label ||
        t('IncludeInCashFlow', 'כלול בתזרים')
      : '',
    ocr: ocrRoutingConfig?.enabled ? `${t('Active')} (${ocrRoutingConfig.rules?.length || 0})` : '',
    documents: totalDocumentsCount > 0 ? String(totalDocumentsCount) : '',
  };

  useEffect(() => {
    const anyError = Object.values(fieldErrors).some(Boolean);
    setIsSaveDisabled(anyError);
  }, [fieldErrors]);

  /* ------------ crud helpers ------------ */
  const checkIfExistingAccountCard = (cards, cardId, caseId, edit = false) => {
    if (!cards || !Array.isArray(cards)) {
      return false;
    }
    
    const byCaseId = cards.filter((c) => caseId && c.accountCaseId === caseId);
    const byCardId = cards.filter((c) => cardId && c.cardId === cardId);

    const caseExists = edit ? byCaseId.length > 1 : byCaseId.length > 0;
    const cardExists = edit ? byCardId.length > 1 : byCardId.length > 0;

    if (caseExists) {
      setMatchingCards(byCaseId);
      setShowAccountCaseIdExistsModal(true);
      return true;
    }
    if (cardExists) {
      toast.info(t('AccountCardExists', { accountCodeId: cardId }));
      return true;
    }
    return false;
  };

  /**
   * Fills the average amount from the card's own ledger (כרטסת) over the last 12 months,
   * so the automatic forecast has a figure to repeat without the user working it out.
   */
  const cashFlowCaseId = client?.caseId || editedItem?.caseId;

  useEffect(() => {
    let cancelled = false;

    const loadCashFlows = async () => {
      if (!open || !cashFlowCaseId || !token) return;
      try {
        const response = await CashFlowForecastService.GetCodes(cashFlowCaseId, token);
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setCashFlowOptions(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to load the cash flows of the case', error);
      }
    };

    loadCashFlows();
    return () => {
      cancelled = true;
    };
  }, [open, cashFlowCaseId, token]);

  const handleCalculateAverageSpend = async () => {
    const caseId = client?.caseId || editedItem?.caseId;
    const cardId = accountCodeId || editedItem?.cardId;

    if (!caseId || !cardId) {
      toast.info(t('AverageSpendNeedsSavedCard', 'יש לשמור את הכרטיס לפני חישוב אוטומטי'));
      return;
    }

    try {
      setAverageCalculating(true);
      setAverageCalcNote('');

      const response = await CashFlowForecastService.GetAverageSpend(
        { CaseId: caseId, CardId: String(cardId), MonthsBack: 12 },
        token
      );
      if (!response.ok) throw new Error('AverageSpend request failed');

      const data = await response.json();
      if (!data?.amount) {
        setAverageCalcNote(t('AverageSpendNoHistory', 'לא נמצאו תנועות ב-12 החודשים האחרונים'));
        return;
      }

      setCashFlowAverageAmount(String(data.amount));
      setAverageCalcNote(
        t('AverageSpendComputed', 'חושב מ-{{count}} תנועות ב-12 החודשים האחרונים', {
          count: data.transactionCount ?? 0,
        })
      );
    } catch (error) {
      console.error('Failed to calculate the cash-flow average spend', error);
      toast.error(t('AverageSpendFailed', 'החישוב האוטומטי נכשל'));
    } finally {
      setAverageCalculating(false);
    }
  };

  const handleAddProceed = async () => {
    try {
      setLoading(true);

      // Upload documents - create one document record per file (no grouping)
      let documents = [];
      if (selectedDocuments && selectedDocuments.length > 0) {
        try {
          // Create one document record per file (each file gets its own row in AccountCardDocument table)
          for (const doc of selectedDocuments) {
            // Upload file and get the S3 key (DB stores key, never the presigned URL)
            const customFileName = doc.customFileName || null;
            const uploadResult = await EnumsService.UploadAccountCardDocument(
              doc.file,
              client.caseId,
              accountCodeId.toString(),
              token,
              customFileName,
              doc.year || null
            );

            // Create one document record per file. Persist the S3 key so the backend
            // can mint a fresh presigned URL on every read.
            documents.push({
              DocumentType: doc.documentTypeEnum,
              Year: doc.year,
              Title: doc.title || doc.customFileName,
              DocumentDate: doc.year ? new Date(doc.year, 11, 31).toISOString() : null, // End of year
              DocumentUrl: uploadResult?.key || null
            });
          }
        } catch (uploadError) {
          console.error("Failed to upload documents:", uploadError);
          toast.error(t("FailedToUploadDocument") || "Failed to upload documents");
          setLoading(false);
          return;
        }
      }

      const ocrRoutingTarget = palCode === 'לקוחות' ? 'credit' : 'debit';
      const ocrApiFields = userTypeContext?.toString() !== Constants.UserType.Client
        ? serializeOcrRoutingForApi(ocrRoutingConfig, ocrRoutingTarget)
        : { ocrRoutingEnabled: false, ocrRoutingRules: [] };

      if (
        ocrApiFields.ocrRoutingEnabled &&
        (!ocrApiFields.ocrRoutingRules || ocrApiFields.ocrRoutingRules.length === 0)
      ) {
        toast.error(t('OcrRoutingSaveValidation') || 'הפעלת ניתוב OCR דורשת לפחות כלל אחד עם תנאי וכרטיס חשבון יעד');
        setLoading(false);
        return;
      }

      const res = await EnumsService.AddAccountCard(
        client.caseId,
        accountCodeId.toString(),
        accountCodeName,
        accountCaseId,
        palCode,
        token,
        phoneNumber || null,
        location || null,
        email || null,
        toNullableInt(sourceDeductionAssociation),
        true,
        exactOccupation || null,
        toNullableInt(deductionType),
        taxAssessor || null,
        deductionValidity || null,
        deductionRate || null,
        externalSoftwareCardNumber || null,
        sortCodeId || null,
        autoProcessed,
        documents.length > 0 ? documents : null,
        paymentTermsType === '' || paymentTermsType == null ? null : Number(paymentTermsType),
        // PaymentTermsCustomDays is the canonical "days" column the backend reads.
        // For preset selections (Net15/30/45/60/90/Immediate) the option value IS the day count;
        // for Custom (999) we use the number the user typed.
        paymentTermsType === '' || paymentTermsType == null
          ? null
          : Number(paymentTermsType) === Constants.PaymentTermsType.Custom
            ? toNullableInt(paymentTermsCustomDays)
            : Number(paymentTermsType) === Constants.PaymentTermsType.Shoteff
              ? null
              : Number(paymentTermsType),
        userTypeContext?.toString() !== Constants.UserType.Client ? toNullableInt(frequency) : null,
        ocrApiFields.ocrRoutingEnabled,
        ocrApiFields.ocrRoutingRules,
        cashFlowApiFields,
        deliveryLocation,
        additionalDetailsApiFields
      );
      if (res.ok) {
        const newCardIdFromServer = await res.json();
        const cardIdToUse = (typeof newCardIdFromServer === 'string' || typeof newCardIdFromServer === 'number')
          ? String(newCardIdFromServer)
          : accountCodeId.toString();
        syncCreatedAccountCardToSharedCache(cardIdToUse);
        const currentUser = await UserService.getCurrentUser();
        await fetchEnums(currentUser, token, client);
        handleChangeAfterAdd(cardIdToUse, type, accountCaseId || undefined);
        toast.success(t('CustomerSupplierAddedSuccessfully'));
      } else {
        toast.error(t('FailedToAddCustomerSupplier'));
      }
    } catch (error) {
      console.log('error', error);
      toast.error(t('FailedToAddCustomerSupplier'));
    }
    setLoading(false);
    onClose();
  };

  const handleAdd = async () => {
    if (checkIfExistingAccountCard(accountCardOptions || [], accountCodeId, accountCaseId)) return;
    await handleAddProceed();
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // CRITICAL: If existingDocuments is empty but we're in edit mode, fetch them first
      // This prevents data loss when existing documents aren't loaded
      let currentExistingDocuments = existingDocuments;
      if (isEdit && existingDocuments.length === 0 && accountCodeId) {
        try {
          const res = await EnumsService.GetAccountCardByCardId(
            client.caseId,
            accountCodeId.toString(),
            token
          );
          if (res.ok) {
            const card = await res.json();
            if (card?.Documents && Array.isArray(card.Documents) && card.Documents.length > 0) {
              currentExistingDocuments = card.Documents;
              setExistingDocuments(card.Documents); // Update state for future use
            }
          }
        } catch (err) {
          console.error("Failed to fetch existing documents:", err);
          // Continue anyway - backend safeguard will prevent deletion
        }
      }

      // Upload new documents - create one document record per file (no grouping)
      let newDocuments = [];
      if (selectedDocuments && selectedDocuments.length > 0) {
        try {
          // Create one document record per file (each file gets its own row in AccountCardDocument table)
          for (const doc of selectedDocuments) {
            // Upload file and get the S3 key (DB stores key, never the presigned URL)
            const customFileName = doc.customFileName || null;
            const uploadResult = await EnumsService.UploadAccountCardDocument(
              doc.file,
              client.caseId,
              accountCodeId.toString(),
              token,
              customFileName,
              doc.year || null
            );

            // Create one document record per file (without Id - will be created as new).
            // We persist the clean S3 key, never the short-lived presigned URL.
            newDocuments.push({
              Id: null, // New document, no ID yet
              DocumentType: doc.documentTypeEnum,
              Year: doc.year,
              Title: doc.title || doc.customFileName,
              DocumentDate: doc.year ? new Date(doc.year, 11, 31).toISOString() : null, // End of year
              DocumentUrl: uploadResult?.key || null
            });
          }
        } catch (uploadError) {
          console.error("Failed to upload documents:", uploadError);
          toast.error(t("FailedToUploadDocument") || "Failed to upload documents");
          setLoading(false);
          return;
        }
      }

      // Combine existing documents (with IDs) and new documents (without IDs)
      // CRITICAL: Existing documents MUST be included to prevent deletion by backend
      // Backend deletes any document not in the DTO, so we must send all existing ones
      const existingDocsWithIds = currentExistingDocuments
        .filter(doc => doc.Id != null && doc.Id !== undefined) // Only include documents with valid IDs
        .map(doc => ({
          Id: doc.Id, // Preserve existing document ID - this prevents deletion
          DocumentType: doc.DocumentType,
          Year: doc.Year,
          Title: doc.Title,
          DocumentDate: doc.DocumentDate,
          DocumentUrl: doc.DocumentUrl || (doc.DocumentUrls && Array.isArray(doc.DocumentUrls) && doc.DocumentUrls.length > 0 ? doc.DocumentUrls[0] : null) // Handle both new and old format
        }));
      
      // Combine: existing documents (with IDs) + new documents (without IDs)
      // Backend will: keep existing ones (by ID), create new ones (no ID)
      let allDocuments = [...existingDocsWithIds, ...newDocuments];
      
      // Debug log to verify documents are being sent
      console.log('Sending documents to backend:', {
        existing: existingDocsWithIds.length,
        new: newDocuments.length,
        total: allDocuments.length,
        existingIds: existingDocsWithIds.map(d => d.Id),
        newIds: newDocuments.map(d => d.Id),
        existingDocumentsState: currentExistingDocuments.length
      });
      
      // Warn if existing documents are missing (could cause data loss)
      if (isEdit && currentExistingDocuments.length === 0 && documentUrls.length > 0) {
        console.warn('WARNING: Existing documents found but existingDocuments state is empty! Backend safeguard will prevent deletion.');
      }

      const ocrRoutingTarget = palCode === 'לקוחות' ? 'credit' : 'debit';
      const ocrApiFields = userTypeContext?.toString() !== Constants.UserType.Client
        ? serializeOcrRoutingForApi(ocrRoutingConfig, ocrRoutingTarget)
        : { ocrRoutingEnabled: false, ocrRoutingRules: [] };

      if (
        ocrApiFields.ocrRoutingEnabled &&
        (!ocrApiFields.ocrRoutingRules || ocrApiFields.ocrRoutingRules.length === 0)
      ) {
        toast.error(t('OcrRoutingSaveValidation') || 'הפעלת ניתוב OCR דורשת לפחות כלל אחד עם תנאי וכרטיס חשבון יעד');
        setLoading(false);
        return;
      }

      const res = await EnumsService.UpdateAccountCard(
        client.caseId,
        oldCardId,
        oldName,
        accountCodeId,
        accountCodeName,
        accountCaseId,
        palCode,
        token,
        phoneNumber || null,
        location || null,
        email || null,
        toNullableInt(sourceDeductionAssociation),
        oldAccountCaseId || null,
        true,
        exactOccupation || null,
        toNullableInt(deductionType),
        taxAssessor || null,
        deductionValidity || null,
        deductionRate || null,
        externalSoftwareCardNumber || null,
        sortCodeId || null,
        autoProcessed,
        allDocuments.length > 0 ? allDocuments : null,
        paymentTermsType === '' || paymentTermsType == null ? null : Number(paymentTermsType),
        // PaymentTermsCustomDays is the canonical "days" column the backend reads.
        paymentTermsType === '' || paymentTermsType == null
          ? null
          : Number(paymentTermsType) === Constants.PaymentTermsType.Custom
            ? toNullableInt(paymentTermsCustomDays)
            : Number(paymentTermsType) === Constants.PaymentTermsType.Shoteff
              ? null
              : Number(paymentTermsType),
        userTypeContext?.toString() !== Constants.UserType.Client ? toNullableInt(frequency) : null,
        ocrApiFields.ocrRoutingEnabled,
        ocrApiFields.ocrRoutingRules,
        true, // UpdatePaymentTerms — account-card modal intentionally edits terms (including clear)
        { ...cashFlowApiFields, UpdateCashFlowConfig: true },
        deliveryLocation,
        additionalDetailsApiFields
      );
      if (res.ok) {
        syncCreatedAccountCardToSharedCache(accountCodeId.toString());
        const currentUser = await UserService.getCurrentUser();
        await fetchEnums(currentUser, token, client);
        handleChangeAfterAdd(accountCodeId.toString(), type);
        toast.success(t('CustomerSupplierUpdatedSuccessfully'));
      } else {
        toast.error(t('FailedToUpdateCustomerSupplier'));
      }
    } catch {
      toast.error(t('FailedToUpdateCustomerSupplier'));
    }
    setLoading(false);
    onClose();
  };

  /* ------------ JSX ------------ */
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 9999 }}
      BackdropProps={{ style: { zIndex: 9999, position: 'fixed' } }}
      PaperProps={{
        className: styles.nestedPaper,
        elevation: 0,
        style: { zIndex: 10000, position: 'relative' },
      }}
    >
      <div className={`${styles.shell} ${dirClass} ${direction === 'rtl' ? fieldStyles.rtl : fieldStyles.ltr}`} dir={direction}>
        <div className={styles.header}>
          <div className={styles.headerSide}>
            <IconButton
              className={`add-account-card-modal-no-drag ${styles.closeIcon}`}
              onClick={(e) => {
                e.stopPropagation();
                onClose?.();
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label={t('Close') || 'Close'}
            >
              <CancelIcon className={styles.closeIcon} />
            </IconButton>
          </div>
          <div id="draggable-dialog-title" className={styles.dragTitle}>
            {!isEdit ? t('AddAccountCard') : t('EditAccountCard')}
            {isDisabled && (
              <span className={styles.disabledHint}> - {t('OnlyExternalNumberIsEditable')}</span>
            )}
          </div>
          <div className={styles.headerSide} />
        </div>

        <DialogContent className={styles.body}>
          <div className={styles.form}>
            {TrialBalanceSum ? (
              <div className={styles.totalAmount}>
                <strong>{t('TotalAmount')}: </strong>
                {TrialBalanceSum}
              </div>
            ) : null}

            <div className={styles.sectionsToolbar}>
              <button type="button" className={styles.linkBtn} onClick={toggleAllSections}>
                {allSectionsOpen ? t('CollapseAll') : t('ExpandAll')}
              </button>
            </div>

            <AccountCardFormSection
              title={t('GeneralDetails')}
              summary={sectionSummaries.general}
              hasError={fieldErrors.cardId || fieldErrors.cardName || fieldErrors.duplicate || fieldErrors.companyId || fieldErrors.palCode}
              {...sectionProps('general')}
            >
              <div className={styles.row}>
                <div className={styles.field}>
                  <span className={`${styles.fieldLabel} ${fieldErrors.duplicate ? styles.fieldLabelError : ''}`}>
                    <Tooltip
                      title={`${t('Card ID')}${fieldErrors.duplicate ? ` - ${t('DuplicateOf')}: ${duplicateCard?.name}` : ''}`}
                      placement="top"
                      arrow
                    >
                      <span>
                        {t('Card ID')} *
                        {fieldErrors.duplicate && ` - ${t('DuplicateOf')}: ${duplicateCard?.name}`}
                      </span>
                    </Tooltip>
                  </span>
                  <TextField
                    fullWidth
                    value={accountCodeId}
                    disabled={isDisabled}
                    onChange={(e) => {
                      setAccountCodeId(e.target.value);
                      if (suggestedCardId && e.target.value === suggestedCardId) {
                        setSuggestedCardId('');
                      }
                    }}
                    variant="standard"
                    className={inputClassName(fieldErrors.cardId || fieldErrors.duplicate)}
                    InputProps={comboInputProps}
                    inputProps={{ style: { textAlign: fieldTextAlign }, type: 'number' }}
                  />
                  {suggestedCardId && !isEdit && (
                    <span
                      className={styles.suggested}
                      onClick={() => {
                        setAccountCodeId(suggestedCardId);
                        setSuggestedCardId('');
                      }}
                    >
                      {t('SuggestedCardId') || 'Suggested'}: {suggestedCardId} — {t('ClickToApply') || 'click to apply'}
                    </span>
                  )}
                  {isDuplicate && isEdit && oldCardId && accountCodeId && client?.caseId && token && (
                    <button
                      type="button"
                      className={styles.secondaryBtn}
                      disabled={isSwapping}
                      onClick={async () => {
                        setIsSwapping(true);
                        try {
                          await AccountCardService.swapAccountCards(client.caseId, oldCardId, accountCodeId, token);
                          toast.success(t('SwapCardsSuccess') || 'Cards swapped successfully.');
                          if (fetchEnums) await fetchEnums();
                          onClose();
                        } catch (err) {
                          toast.error(err?.message || t('SwapCardsFailed') || 'Failed to swap cards.');
                        } finally {
                          setIsSwapping(false);
                        }
                      }}
                      style={{ marginTop: 8 }}
                    >
                      {isSwapping ? (t('Swapping') || 'Swapping...') : (t('SwapWithThisCard') || 'Swap with this card')}
                    </button>
                  )}
                </div>

                <div className={styles.field}>
                  <span className={`${styles.fieldLabel} ${fieldErrors.palCode ? styles.fieldLabelError : ''}`}>
                    {t('PalCode')}{requirePalCode ? ' *' : ''}
                  </span>
                  <FilterSelect
                    direction={direction}
                    options={formattedPalCodeOptions}
                    selectedValues={formattedPalCodeOptions.find((o) => o.value === palCode) || null}
                    onChange={async (_, v) => {
                      const newPalCode = v ? v.value : '';
                      setPalCode(newPalCode);
                      setSuggestedCardId('');
                      if (!isEdit && client?.caseId && token && newPalCode) {
                        try {
                          const res = await AccountCardService.getNextCardId(client.caseId, newPalCode, token);
                          if (res?.cardId) {
                            const nextId = String(res.cardId);
                            if (!accountCodeId || accountCodeId.trim() === '') {
                              setAccountCodeId(nextId);
                            } else {
                              setSuggestedCardId(nextId);
                            }
                          }
                        } catch (err) {
                          console.warn('Could not get next card ID for PalCode:', err);
                        }
                      }
                    }}
                    placeholder={t('PalCode')}
                  />
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('Card Name')} *</span>
                  {isDepreciationAccount && assetGroups.length > 0 ? (
                    <Autocomplete
                      disabled={isDisabled}
                      freeSolo
                      className={fieldStyles.combo}
                      options={assetGroups}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return option.assetGroupName || '';
                      }}
                      value={accountCodeName}
                      inputValue={accountCodeName}
                      onChange={(_, newValue) => {
                        if (typeof newValue === 'string') {
                          setAccountCodeName(newValue);
                          const matchingGroup = assetGroups.find((g) =>
                            newValue.includes(g.assetGroupName)
                          );
                          setSelectedAssetGroup(matchingGroup || null);
                        } else if (newValue && newValue.assetGroupName) {
                          const fullName = `${namePrefix}${newValue.assetGroupName}`;
                          setAccountCodeName(fullName);
                          setSelectedAssetGroup(newValue);
                        } else {
                          setAccountCodeName(namePrefix);
                          setSelectedAssetGroup(null);
                        }
                      }}
                      onInputChange={(_, newInputValue, reason) => {
                        if (reason === 'input' || reason === 'clear') {
                          setAccountCodeName(newInputValue);
                          const matchingGroup = assetGroups.find((g) =>
                            newInputValue.includes(g.assetGroupName)
                          );
                          setSelectedAssetGroup(matchingGroup || null);
                        }
                      }}
                      filterOptions={(options, params) => {
                        const searchValue = params.inputValue.toLowerCase();
                        const prefixLower = namePrefix.toLowerCase();
                        return options.filter((option) => {
                          const groupName = option.assetGroupName?.toLowerCase() || '';
                          const searchWithoutPrefix = searchValue.replace(prefixLower, '').trim();
                          return groupName.includes(searchWithoutPrefix) ||
                            groupName.includes(searchValue) ||
                            `${prefixLower}${groupName}`.includes(searchValue);
                        });
                      }}
                      renderOption={(props, option) => {
                        const displayText = typeof option === 'string'
                          ? option
                          : `${namePrefix}${option.assetGroupName || ''}`;
                        return (
                          <li {...props} key={option.assetGroupId || option}>
                            {displayText}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder={t('TypeOrSelectAssetGroup') || `${namePrefix}Type or select asset group`}
                          className={inputClassName(fieldErrors.cardName)}
                          InputProps={{ ...params.InputProps, ...comboInputProps }}
                          inputProps={{ ...params.inputProps, style: { textAlign: fieldTextAlign } }}
                        />
                      )}
                      PopperComponent={renderComboPopper}
                    />
                  ) : (
                    <TextField
                      disabled={isDisabled}
                      fullWidth
                      value={accountCodeName}
                      onChange={(e) => setAccountCodeName(e.target.value)}
                      variant="standard"
                      className={inputClassName(fieldErrors.cardName)}
                      InputProps={comboInputProps}
                      inputProps={{ style: { textAlign: fieldTextAlign } }}
                    />
                  )}
                </div>

                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('CompanyId')}</span>
                  <TextField
                    disabled={isDisabled}
                    fullWidth
                    value={accountCaseId}
                    onChange={(e) => setAccountCaseId(e.target.value)}
                    variant="standard"
                    className={inputClassName(fieldErrors.companyId)}
                    InputProps={comboInputProps}
                    inputProps={{ style: { textAlign: fieldTextAlign } }}
                  />
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('ExactOccupation')}</span>
                  <TextField
                    disabled={isDisabled}
                    fullWidth
                    value={exactOccupation}
                    onChange={(e) => setExactOccupation(e.target.value)}
                    variant="standard"
                    className={inputClassName()}
                    InputProps={comboInputProps}
                    inputProps={{ style: { textAlign: fieldTextAlign } }}
                  />
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('ExternalSoftwareCardNumber')}</span>
                  <TextField
                    fullWidth
                    value={externalSoftwareCardNumber}
                    onChange={(e) => setExternalSoftwareCardNumber(e.target.value)}
                    variant="standard"
                    type="number"
                    className={inputClassName()}
                    InputProps={comboInputProps}
                    inputProps={{ style: { textAlign: fieldTextAlign } }}
                  />
                </div>
              </div>
            </AccountCardFormSection>

            <AccountCardFormSection
              title={t('ContactDetails')}
              summary={sectionSummaries.contact}
              hasError={fieldErrors.phone || fieldErrors.phone2 || fieldErrors.email}
              {...sectionProps('contact')}
            >
              <div className={styles.row}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('PhoneNumber')}</span>
                  <TextField
                    disabled={isDisabled}
                    fullWidth
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    variant="standard"
                    className={inputClassName(fieldErrors.phone)}
                    InputProps={comboInputProps}
                    inputProps={{ style: { textAlign: fieldTextAlign }, type: 'number' }}
                  />
                </div>

                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('SecondPhoneNumber')}</span>
                  <TextField
                    disabled={isDisabled}
                    fullWidth
                    value={phoneNumber2}
                    onChange={(e) => setPhoneNumber2(e.target.value)}
                    variant="standard"
                    className={inputClassName(fieldErrors.phone2)}
                    InputProps={comboInputProps}
                    inputProps={{ style: { textAlign: fieldTextAlign }, type: 'number' }}
                  />
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('Email')}</span>
                  <TextField
                    disabled={isDisabled}
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    variant="standard"
                    className={inputClassName(fieldErrors.email)}
                    InputProps={comboInputProps}
                    inputProps={{ style: { textAlign: fieldTextAlign }, type: 'email' }}
                  />
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('Location')}</span>
                  <TextField
                    disabled={isDisabled}
                    fullWidth
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    variant="standard"
                    className={inputClassName()}
                    InputProps={comboInputProps}
                    inputProps={{ style: { textAlign: fieldTextAlign } }}
                  />
                </div>

                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('ShippingAddress')}</span>
                  <TextField
                    disabled={isDisabled}
                    fullWidth
                    value={deliveryLocation}
                    onChange={(e) => setDeliveryLocation(e.target.value)}
                    variant="standard"
                    className={inputClassName()}
                    InputProps={comboInputProps}
                    inputProps={{ style: { textAlign: fieldTextAlign } }}
                    placeholder={t('ShippingAddress')}
                  />
                </div>
              </div>
            </AccountCardFormSection>

            <AccountCardFormSection
              title={t('BankDetails')}
              summary={sectionSummaries.bank}
              {...sectionProps('bank')}
            >
              <div className={styles.row}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('BankCode')}</span>
                  <FilterSelect
                    direction={direction}
                    options={bankFilterOptions}
                    selectedValues={bankFilterOptions.find((o) => o.value === bankCode) || null}
                    onChange={(_, v) => {
                      setBankCode(v ? v.value : '');
                      const bank = v ? Constants.ISRAEL_BANKS.find((b) => b.code === v.value) : null;
                      if (bank) setBankName(bank.name);
                      else if (!v) setBankName('');
                    }}
                    placeholder={t('BankCode')}
                    isDisabled={isDisabled}
                  />
                </div>

                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('BankName')}</span>
                  <TextField
                    disabled={isDisabled}
                    fullWidth
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    variant="standard"
                    className={inputClassName()}
                    InputProps={comboInputProps}
                    inputProps={{ style: { textAlign: fieldTextAlign } }}
                  />
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('BranchNumber')}</span>
                  <TextField
                    disabled={isDisabled}
                    fullWidth
                    value={branchCode}
                    onChange={(e) => setBranchCode(e.target.value)}
                    variant="standard"
                    className={inputClassName()}
                    InputProps={comboInputProps}
                    inputProps={{ style: { textAlign: fieldTextAlign }, inputMode: 'numeric' }}
                  />
                </div>

                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('AccountNumber')}</span>
                  <TextField
                    disabled={isDisabled}
                    fullWidth
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    variant="standard"
                    className={inputClassName()}
                    InputProps={comboInputProps}
                    inputProps={{ style: { textAlign: fieldTextAlign }, inputMode: 'numeric' }}
                  />
                </div>
              </div>
            </AccountCardFormSection>

            <AccountCardFormSection
              title={t('WithholdingTax')}
              summary={sectionSummaries.withholding}
              {...sectionProps('withholding')}
            >
              <div className={styles.row}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('SourceDeductionAssociation')}</span>
                  <FilterSelect
                    direction={direction}
                    options={sourceDeductionFilterOptions}
                    selectedValues={
                      sourceDeductionAssociation === '' || sourceDeductionAssociation == null
                        ? null
                        : sourceDeductionFilterOptions.find(
                            (o) => Number(o.value) === Number(sourceDeductionAssociation)
                          ) || null
                    }
                    onChange={(_, v) => setSourceDeductionAssociation(v ? v.value : '')}
                    placeholder={t('SourceDeductionAssociation')}
                    isDisabled={isDisabled}
                  />
                </div>

                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('DeductionType')}</span>
                  <FilterSelect
                    direction={direction}
                    options={Constants.DeductionTypeOptions}
                    selectedValues={
                      deductionType === '' || deductionType == null
                        ? null
                        : Constants.DeductionTypeOptions.find(
                            (o) => Number(o.value) === Number(deductionType)
                          ) || null
                    }
                    onChange={(_, v) => setDeductionType(v ? v.value : '')}
                    placeholder={t('DeductionType')}
                    isDisabled={isDisabled}
                  />
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('DeductionRate')}</span>
                  <TextField
                    disabled={isDisabled}
                    fullWidth
                    value={deductionRate}
                    onChange={(e) => setDeductionRate(e.target.value)}
                    variant="standard"
                    className={inputClassName()}
                    InputProps={comboInputProps}
                    inputProps={{ style: { textAlign: fieldTextAlign }, type: 'number', step: '0.01' }}
                  />
                </div>

                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('DeductionValidity')}</span>
                  <CustomDatePicker
                    disabled={isDisabled}
                    date={deductionValidity ? deductionValidity : null}
                    onDateChange={(d) => setDeductionValidity(d ? d.toISOString() : null)}
                    height="40px"
                    textFieldClassName={fieldStyles.styleInput}
                    inputSx={datePickerInputSx}
                  />
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('TaxAssessor')}</span>
                  <FilterSelect
                    direction={direction}
                    options={taxAssessorFilterOptions}
                    selectedValues={
                      taxAssessorFilterOptions.find((o) => o.value === taxAssessor) || null
                    }
                    onChange={(_, v) => setTaxAssessor(v ? v.value : '')}
                    placeholder={t('TaxAssessor')}
                    isDisabled={isDisabled}
                  />
                </div>
                <div className={styles.field} />
              </div>
            </AccountCardFormSection>

            <AccountCardFormSection
              title={t('PaymentTerms')}
              summary={sectionSummaries.paymentTerms}
              {...sectionProps('paymentTerms')}
            >
              <div className={styles.row}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('PaymentTerms')}</span>
                  <FilterSelect
                    direction={direction}
                    options={paymentTermsFilterOptions}
                    selectedValues={
                      paymentTermsType === '' || paymentTermsType == null
                        ? null
                        : paymentTermsFilterOptions.find(
                            (o) => Number(o.value) === Number(paymentTermsType)
                          ) || null
                    }
                    onChange={(_, v) => {
                      setPaymentTermsType(v ? v.value : '');
                      if (!v || Number(v.value) !== Constants.PaymentTermsType.Custom) {
                        setPaymentTermsCustomDays('');
                      }
                    }}
                    placeholder={t('PaymentTerms_Placeholder', 'e.g. Net 30 days')}
                    isDisabled={isDisabled}
                  />
                  {Number(paymentTermsType) === Constants.PaymentTermsType.Shoteff && (
                    <span className={styles.hint}>
                      {t('PaymentTerms_Shoteff_Hint', 'Due by the last day of the document month')}
                    </span>
                  )}
                </div>

                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t('PaymentTermsCustomDays', 'Custom days')}</span>
                  <TextField
                    disabled={isDisabled || Number(paymentTermsType) !== Constants.PaymentTermsType.Custom}
                    fullWidth
                    value={paymentTermsCustomDays}
                    onChange={(e) => setPaymentTermsCustomDays(e.target.value)}
                    variant="standard"
                    placeholder={t('PaymentTermsCustomDaysPlaceholder', 'Days until due')}
                    className={inputClassName()}
                    InputProps={comboInputProps}
                    inputProps={{ style: { textAlign: fieldTextAlign }, type: 'number', min: 0 }}
                  />
                </div>
              </div>
            </AccountCardFormSection>

            {!isClientUser && (
              <AccountCardFormSection
                title={t('Classification')}
                summary={sectionSummaries.classification}
                {...sectionProps('classification')}
              >
                <div className={styles.row}>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>{t('SortCode')}</span>
                    <FilterSelect
                      direction={direction}
                      options={formattedSortCodeOptions}
                      selectedValues={
                        formattedSortCodeOptions.find(
                          (o) => String(o.value) === String(sortCodeId)
                        ) || null
                      }
                      onChange={(_, v) => {
                        const id = v == null ? '' : (v.value ?? v.sortCodeId ?? v.SortCodeId ?? v.id ?? v.Id ?? '');
                        setSortCodeId(id !== '' && id != null ? String(id) : '');
                      }}
                      placeholder={t('SortCode')}
                      isDisabled={isDisabled}
                    />
                  </div>
                  <div className={`${styles.field} ${styles.checkboxField}`}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={autoProcessed}
                          onChange={(e) => setAutoProcessed(e.target.checked)}
                          disabled={isDisabled}
                        />
                      }
                      label={t('AutoProcessed')}
                    />
                  </div>
                </div>
              </AccountCardFormSection>
            )}

            {!isClientUser && (
              <AccountCardFormSection
                title={t('CashFlowSectionTitle', 'תזרים מזומנים')}
                summary={sectionSummaries.cashFlow}
                {...sectionProps('cashFlow')}
              >
                <div className={styles.hint}>
                  {t(
                    'CashFlowSectionHint',
                    'כרטיסים שמסומנים כאן משתתפים בדוח תזרים המזומנים. תדירות + ממוצע הוצאה מייצרים תחזית אוטומטית לתקופות הבאות.'
                  )}
                </div>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeInCashFlow}
                      onChange={(e) => setIncludeInCashFlow(e.target.checked)}
                    />
                  }
                  label={t('IncludeInCashFlow', 'כלול בתזרים')}
                />

                {includeInCashFlow && (
                  <>
                    <div className={styles.row}>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>{t('CashFlowCode', 'קוד תזרים')}</span>
                        <FilterSelect
                          direction={direction}
                          options={cashFlowFilterOptions}
                          selectedValues={
                            cashFlowCode === '' || cashFlowCode == null
                              ? null
                              : cashFlowFilterOptions.find((o) => Number(o.value) === Number(cashFlowCode)) || null
                          }
                          onChange={(_, v) => setCashFlowCode(v ? v.value : '')}
                          placeholder={t('SelectCashFlow', 'בחרו תזרים')}
                        />
                        <span className={styles.hint}>
                          {t(
                            'CashFlowCodeFromSettingsHint',
                            'התזרימים מוגדרים במסך תזרים המזומנים תחת "הגדרות תזרים".'
                          )}
                        </span>
                      </div>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>{t('ExpenseFrequency')}</span>
                        <FilterSelect
                          direction={direction}
                          options={frequencyFilterOptions}
                          selectedValues={
                            frequency === '' || frequency == null
                              ? null
                              : frequencyFilterOptions.find(
                                  (o) => Number(o.value) === Number(frequency)
                                ) || null
                          }
                          onChange={(_, v) => setFrequency(v ? v.value : '')}
                          placeholder={t('ExpenseFrequency_Placeholder')}
                        />
                      </div>
                    </div>

                    <div className={styles.row}>
                      <div className={`${styles.field} ${styles.fieldGrow}`}>
                        <span className={styles.fieldLabel}>
                          {t('CashFlowAverageAmount', 'ממוצע הוצאה בתקופה')}
                        </span>
                        <TextField
                          fullWidth
                          type="number"
                          value={cashFlowAverageAmount}
                          onChange={(e) => {
                            setCashFlowAverageAmount(e.target.value);
                            setAverageCalcNote('');
                          }}
                          variant="standard"
                          placeholder={t('CashFlowAverageAmountPlaceholder', 'לדוגמה: 2000')}
                          className={inputClassName()}
                          InputProps={comboInputProps}
                          inputProps={{ style: { textAlign: fieldTextAlign } }}
                        />
                        <span className={styles.hint}>
                          {averageCalcNote ||
                            t(
                              'CashFlowAverageAmountHint',
                              'הסכום שיוצג בכל תקופה לפי התדירות, ללא תאריך התחלה או סיום. אם השדה ריק — התזרים יחשב ממוצע מהכרטסת.'
                            )}
                        </span>
                      </div>
                      <div className={styles.field} style={{ justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          className={styles.secondaryBtn}
                          onClick={handleCalculateAverageSpend}
                          disabled={averageCalculating}
                          style={{ width: '100%', marginTop: 22 }}
                        >
                          {averageCalculating
                            ? t('Calculating', 'מחשב...')
                            : t('CalculateAverageFromLedger', 'חשב אוטומטית מהכרטסת')}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </AccountCardFormSection>
            )}

            {!isClientUser && (
              <AccountCardFormSection
                title={t('OcrRoutingRulesTitle')}
                summary={sectionSummaries.ocr}
                {...sectionProps('ocr')}
              >
                <div className={styles.row}>
                  <div className={styles.field}>
                    <div className={styles.ocrWrap}>
                      <OcrRoutingRulesEditor
                        value={ocrRoutingConfig}
                        onChange={setOcrRoutingConfig}
                        disabled={isDisabled}
                        direction={direction}
                        defaultTarget={palCode === 'לקוחות' ? 'credit' : 'debit'}
                        caseId={client?.caseId || editedItem?.caseId}
                        token={token}
                        accountCardOptions={accountCardOptions}
                        sortCodeOptions={formattedSortCodeOptions}
                      />
                    </div>
                  </div>
                </div>
              </AccountCardFormSection>
            )}

            <AccountCardFormSection
              title={t('DocumentUpload') || 'צירוף מסמכים'}
              summary={sectionSummaries.documents}
              {...sectionProps('documents')}
            >
              <div className={styles.row}>
                <div className={styles.field}>
                  <div className={styles.docHeader}>
                    {selectedDocuments.length > 0 && (
                      <span className={styles.unsavedNote}>
                        {t('YouNeedToSaveTheDocumentYouUploaded') + ' *'}
                      </span>
                    )}
                  </div>

                  <div className={styles.tableWrap}>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {Constants.AccountCardDocumentTypes.map((docType) => (
                              <TableCell key={docType.enumValue}>{docType.label}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            {Constants.AccountCardDocumentTypes.map((docType) => {
                              const count = getDocumentCountsByType.counts[docType.enumValue] || 0;
                              const unsavedCount = getDocumentCountsByType.unsavedCounts[docType.enumValue] || 0;
                              const hasUnsaved = unsavedCount > 0;
                              return (
                                <TableCell
                                  key={docType.enumValue}
                                  className={styles.docCell}
                                  onClick={() => handleOpenDocumentsByType(docType.enumValue)}
                                >
                                  <span
                                    className={`${styles.docCount} ${hasUnsaved ? styles.docCountUnsaved : count > 0 ? styles.docCountHas : ''}`}
                                  >
                                    {count}
                                  </span>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>

                  <input
                    type="file"
                    ref={documentInputRef}
                    onChange={handleDocumentChange}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                  />

                  <div className={styles.uploadRow}>
                    <button
                      type="button"
                      className={`${styles.secondaryBtn} ${styles.compactBtn}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDocumentClick();
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: 16 }} />
                      {t('ChooseDocuments') || 'Choose Documents'}
                    </button>
                  </div>
                </div>
              </div>
            </AccountCardFormSection>
          </div>
        </DialogContent>

        <DialogActions className={styles.footer}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose}>
            {t('Cancel')}
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={!isSaveDisabled ? (isEdit ? handleSave : handleAdd) : undefined}
            disabled={isSaveDisabled}
          >
            {t('Save')}
          </button>
        </DialogActions>
      </div>

      {showAccountCaseIdExistsModal && (
        <Dialog
          open={showAccountCaseIdExistsModal}
          onClose={() => setShowAccountCaseIdExistsModal(false)}
          maxWidth="sm"
          fullWidth
          sx={{ zIndex: 10001 }}
          PaperProps={{
            className: styles.nestedPaper,
            elevation: 0,
            style: { zIndex: 10002, position: 'relative' },
          }}
        >
          <div className={`${styles.shell} ${dirClass} ${direction === 'rtl' ? fieldStyles.rtl : fieldStyles.ltr}`} dir={direction}>
            <div className={styles.header}>
              <IconButton
                className={styles.closeIcon}
                onClick={() => setShowAccountCaseIdExistsModal(false)}
              >
                <CancelIcon className={styles.closeIcon} />
              </IconButton>
              <div>{t('AccountIdAlreadyExists')}</div>
              <div />
            </div>
            <DialogContent className={styles.confirmBody}>
              <div className={styles.confirmText}>{t('ConfirmOpenAccountCard')}</div>
              {matchingCards.length > 0 && (
                <div>
                  <div className={styles.existingTitle}>
                    {t('ExistingCardsWithSameAccountCaseId')}:
                  </div>
                  <div className={styles.tableWrap}>
                    <table className={styles.existingTable}>
                      <thead>
                        <tr>
                          <th>{t('Card Name')}</th>
                          <th>{t('CompanyId')}</th>
                          <th>{t('PalCode')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matchingCards.map((card, index) => (
                          <tr key={card.cardId || index}>
                            <td>{card.name || card.accountCodeName || '-'}</td>
                            <td>{card.accountCaseId || '-'}</td>
                            <td>{card.palCode || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </DialogContent>
            <DialogActions className={styles.footer}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => {
                  setShowAccountCaseIdExistsModal(false);
                  setMatchingCards([]);
                }}
              >
                {t('No')}
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => {
                  setShowAccountCaseIdExistsModal(false);
                  setMatchingCards([]);
                  handleAddProceed();
                }}
              >
                {t('Yes')}
              </button>
            </DialogActions>
          </div>
        </Dialog>
      )}

      <DocumentTypeYearModal
        open={showDocumentTypeModal}
        onClose={() => {
          setShowDocumentTypeModal(false);
          setPendingFiles([]);
        }}
        onConfirm={handleDocumentTypeConfirm}
        direction={direction}
        pendingFiles={pendingFiles}
      />

      {showDocumentsByTypeModal && selectedDocumentType !== null && (
        <DocumentsByTypeModal
          key={`documents-modal-${selectedDocumentType}-${existingDocuments.length}-${selectedDocuments.length}`}
          open={showDocumentsByTypeModal}
          onClose={() => {
            setShowDocumentsByTypeModal(false);
            setSelectedDocumentType(null);
            setModalPreviewIndex(0);
          }}
          selectedDocumentType={selectedDocumentType}
          documentsByType={getDocumentsByType(selectedDocumentType)}
          direction={direction}
          formatFileSize={formatFileSize}
          onRemoveDocument={removeDocument}
          onRemoveExistingDocument={removeExistingDocument}
          onFileSelect={(e) => handleDocumentChangeForType(e, selectedDocumentType)}
          onDocumentClick={(doc) => {
            const allDocs = getAllDocuments();
            const docIndex = allDocs.findIndex((d) =>
              d.id === doc.id || (doc.isExisting && d.url === doc.url)
            );
            if (docIndex >= 0) {
              setCurrentDocIndex(docIndex);
            }
          }}
        />
      )}
    </Dialog>
  );
};

export default AddAccountCardModal;
